import { newGuestExperience } from "@/services/homepageExperiment";
import { AvatarTypes, LobbyRoom, FREE_TIER } from "@chesshotel/constants";
import { createReduxModule } from "hooks-for-redux";
import { trackGuestSessionStarted } from "../services/eventTracker";
import { readGuestIdentity, rememberGuestIdentity, clearStoredGuestIdentity, GuestIdentity } from "../util/guestIdentity";
// import { setTier } from "./membership";

interface UserData {
  username: string;
  isGuest: boolean;
  isDefaultGuest?: boolean;
  generatedGuestName?: boolean;
  persistentGuest?: boolean;
  id: number;
  room: LobbyRoom;
  avatarId: number;
  avatarType: AvatarTypes;
  fbId: number;
  googleId?: string;
  email: string;
  hasRecoveryCode?: boolean;
  created: Date;
  country: string;
  activated?: number;
}

const initialState = {
  memberLoggedIn: false,
  serverLoggedIn: false,
  isDefaultGuest: true,
  generatedGuestName: false,
  persistentGuest: false,
  guestInMemberRoom: false,
  displayName: null,
  isGuest: null,
  username: null,
  created: null,
  country: null,
  room: null,
  fbId: null,
  googleId: null,
  id: null,
  avatarId: null,
  avatarType: AvatarTypes.GUEST,
  avatarUpdateCount: 0,
  emailNotActivated: false,
  hasRecoveryCode: false,
  email: null,
  geo: null,
};

export const [
  useUser,
  {
    setUser,
    updateUsername,
    serverLoggedIn,
    setFbId,
    setAvatarId,
    setAvatarUpload,
    resetUser,
    setEmailNotActivated,
    setGeo,
  },
  userStore,
] = createReduxModule("user", initialState, {
  setUser: (state, userData: UserData) => ({
    ...state,
    ...userData,
    memberLoggedIn: !userData.isGuest,
    avatarType: userData.avatarType || AvatarTypes.GUEST,
    displayName: userData.isGuest
      ? userData.username.substring(2)
      : userData.username,
    // guestInMemberRoom: userData.isGuest && userData.room === ROOM.MEMBERS,
    isDefaultGuest: userData.isGuest && (userData.isDefaultGuest ?? userData.username.startsWith("G*Guest")),
    generatedGuestName: userData.isGuest && userData.generatedGuestName === true,
    emailNotActivated: userData.email && userData.activated != 1,
    email: userData.email,
  }),
  setGeo: (state, geo: string) => ({
    ...state,
    geo,
  }),
  updateUsername: (state, username) => ({
    ...state,
    username,
    displayName: state.isGuest ? username.substring(2) : username,
  }),
  serverLoggedIn: (state, loggedIn) => ({ ...state, serverLoggedIn: loggedIn }),
  setFbId: (state, fbId) => ({
    ...state,
    fbId,
    avatarType: AvatarTypes.FACEBOOK,
    avatarUpdateCount: state.avatarUpdateCount + 1,
  }),
  setAvatarId: (state, avatarId) => ({
    ...state,
    avatarId,
    avatarType: AvatarTypes.DEFAULT,
    avatarUpdateCount: state.avatarUpdateCount + 1,
  }),
  setAvatarUpload: (state) => ({
    ...state,
    avatarType: AvatarTypes.UPLOAD,
    avatarUpdateCount: state.avatarUpdateCount + 1,
  }),
  resetUser: () => initialState,
  setEmailNotActivated: (state, emailNotActivated: boolean) => ({
    ...state,
    emailNotActivated,
  }),
});

export const LOGIN_GUEST = "LOGIN_GUEST";

let usernameCount = 2;
let atemptedUsername = null;

function initWebsocketConnectLazy() {
  import("../services/websocket").then((module) => {
    module.initWebsocketConnect();
  });
}

export async function loginGuest(
  username,
  options: { preferStoredName?: boolean; generated?: boolean; chosen?: boolean } = {}
) {
  // const guests = getState().siteData.guestsOnline;
  // const members = getState().siteData.membersOnline;
  // const gotoMembersRoom = (guests - 200) > members && username && username.substring(0, 5) !== "Guest";

  atemptedUsername = "G*" + username;
  const isGuest = true;
  const room = LobbyRoom.MEMBERS; // gotoMembersRoom ? ROOM.MEMBERS : ROOM.GUESTS;
  let userId = Math.round(Math.random() * -1000000);

  let identity: GuestIdentity;
  let guest: import("../services/guestSession").GuestSnapshot | null = null;
  let session: typeof import("../services/guestSession");
  if (!newGuestExperience()) {
    // Preserve the deployed tab-only naming behaviour for control and flags off.
    let remembered: string | null = null;
    try { if (options.preferStoredName) remembered = sessionStorage.getItem("guestUsername"); } catch {}
    const name = remembered?.replace(/^G\*/, "") || username;
    identity = { name, generated: false, chosen: !name.startsWith("Guest") };
    atemptedUsername = `G*${identity.name}`;
    try { if (!options.preferStoredName) sessionStorage.setItem("guestUsername", atemptedUsername); } catch {}
  } else {
    identity = (options.preferStoredName && readGuestIdentity()) || {
      name: username, generated: options.generated === true,
      chosen: options.chosen ?? options.generated !== true,
    };
    session = await import("../services/guestSession");
    try { guest = await session.getGuestSession(identity); } catch { /* Existing feature gate and fallback. */ }
    if (userStore.getState().memberLoggedIn) return;
    if (guest) {
      if (!options.preferStoredName && options.chosen && guest.name !== identity.name) {
        const error = await session.renamePersistentGuest(identity);
        if (!error) guest = { ...guest, ...identity };
      }
      identity = { name: guest.name, generated: guest.generated, chosen: guest.chosen };
      userId = guest.id;
    }
    atemptedUsername = `G*${identity.name}`;
    rememberGuestIdentity(identity);
  }

  // loginServer(atemptedUsername, isGuest, userId, room);

  setUser({
    username: atemptedUsername,
    isGuest,
    isDefaultGuest: !identity.chosen,
    generatedGuestName: identity.generated,
    persistentGuest: !!guest,
    id: userId,
    room,
    avatarId: null,
    fbId: null,
    avatarType: AvatarTypes.GUEST,
    hasRecoveryCode: false,
    email: null,
    created: null,
    country: null,
    activated: null,
  });

  if (guest) { session.applyGuestSnapshot(guest); await session.restoreGuestProgress(guest); }
  trackGuestSessionStarted();
  import("./membership").then(({ setTier }) => setTier(FREE_TIER));
  // setTier(FREE_TIER);

  // connectToWebSocket(() =>
  //   loginServer(atemptedUsername, isGuest, userId, room)
  // );
  initWebsocketConnectLazy();
}

export async function duplicateUser() {
  const user = userStore.getState();
  if (newGuestExperience() && user.generatedGuestName) {
    const { availableGuestName } = await import("../util/guestNames");
    const name = await availableGuestName([user.displayName]);
    const identity = { name, generated: true, chosen: !user.isDefaultGuest };
    if (user.persistentGuest) {
      const { renamePersistentGuest } = await import("../services/guestSession");
      if (await renamePersistentGuest(identity)) return;
    } else applyGuestIdentity(identity);
  } else {
    const username = atemptedUsername + usernameCount++;
    const identity = { name: username.substring(2).slice(0, 18), generated: false, chosen: true };
    if (user.persistentGuest) {
      const { renamePersistentGuest } = await import("../services/guestSession");
      if (await renamePersistentGuest(identity)) return;
    } else applyGuestIdentity(identity);
  }
  initWebsocketConnectLazy();
}

/** Change identity without replacing the guest's id, rating, or browser progress. */
export function applyGuestIdentity(identity: GuestIdentity) {
  const user = userStore.getState();
  if (!user.isGuest) return;
  atemptedUsername = `G*${identity.name}`;
  const chosen = newGuestExperience() ? identity.chosen : !identity.name.startsWith("Guest");
  setUser({ ...user, username: atemptedUsername, isDefaultGuest: !chosen, generatedGuestName: newGuestExperience() && identity.generated });
  if (newGuestExperience()) rememberGuestIdentity(identity);
  else { try { sessionStorage.setItem("guestUsername", atemptedUsername); } catch {} }
}

export function saveGuestName() {
  const user = userStore.getState();
  if (!user.isGuest) return;
  if (newGuestExperience()) rememberGuestIdentity({ name: user.displayName, generated: user.generatedGuestName, chosen: !user.isDefaultGuest });
  else { try { sessionStorage.setItem("guestUsername", user.username); } catch {} }
}

export function clearGuestName() {
  clearStoredGuestIdentity();
}

/** Control keeps its deployed reset; treatment carries the name into signup. */
export function finishGuestGameIdentity() {
  if (!userStore.getState().isGuest) return;
  if (newGuestExperience()) saveGuestName();
  else clearGuestName();
}
